import { Button } from '@base-ui/react/button';
import { faBookOpen, faFileLines } from '@fortawesome/free-solid-svg-icons';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { apiErrorDetail } from '@/admin/errorDetail';
import buttonStyles from '@/admin/styles/button.module.css';
import { ConfirmDialog } from '@/admin/ui/ConfirmDialog';
import {
  CourseOutlineLayout,
  courseOf,
  TopicHeader,
} from '@/admin/ui/ContentNav';
import { DraftAncestorNotice } from '@/admin/ui/DraftAncestorNotice';
import {
  DangerGroup,
  DetailPage,
  EditorLayout,
  EditorMain,
  EditorPanel,
  FormSection,
  PanelGroup,
  PanelLabel,
  SaveGroup,
} from '@/admin/ui/Editor';
import { useEditMode, validateEditSearch } from '@/admin/ui/editSearch';
import { Field, FieldLabel, FieldValue } from '@/admin/ui/FieldDisplay';
import {
  ErrorText,
  FieldHint,
  FormLabel,
  FormRow,
  Select,
  TextInput,
} from '@/admin/ui/Form';
import { QuizPublishWarning, quizPublishMessage } from '@/admin/ui/QuizLock';
import { richContentCss } from '@/admin/ui/RichContent';
import { RichTextEditor } from '@/admin/ui/RichTextEditor';
import { FieldGrid, Section, SectionTitle } from '@/admin/ui/Section';
import { useUnsavedChanges } from '@/admin/ui/useUnsavedChanges';
import {
  clampPosition,
  deletePrompt,
  EditConflictNotice,
  editConflict,
  LiveEditNotice,
  NeedsNewVersion,
  useCourseVersions,
} from '@/admin/ui/Versions';
import api, { queryClient } from '@/api';
import type { components } from '@/api/qraft/schema';

type TopicAdmin = components['schemas']['TopicAdmin'];

export const Route = createFileRoute('/admin/topics/$topicId')({
  component: TopicDetail,
  validateSearch: validateEditSearch,
});

function TopicDetail() {
  const { t } = useTranslation();
  const { topicId } = Route.useParams();
  const topicIdNum = Number(topicId);
  const { edit } = Route.useSearch();
  const { editing, startEditing, stopEditing } = useEditMode(edit === true);

  const admin = api.adminGetTopic.useQuery({ path: { topic_id: topicIdNum } });

  if (admin.isLoading) return <Message>{t('admin.topics.loadingOne')}</Message>;
  if (admin.error || !admin.data)
    return <Message $error>{t('admin.topics.errorLoadingOne')}</Message>;

  const contentHtml = admin.data.content;

  return (
    <DetailPage>
      <TopicHeader
        topic={admin.data}
        nav={!editing}
        action={
          !editing && (
            <Button className={buttonStyles.Button} onClick={startEditing}>
              {t('admin.common.edit')}
            </Button>
          )
        }
      />
      {editing ? (
        // key remounts the form when switching topics, so its seeded state stays fresh.
        <TopicEditor
          key={admin.data.id}
          topic={admin.data}
          onDone={stopEditing}
        />
      ) : (
        <CourseOutlineLayout
          course={courseOf(admin.data.path)}
          lesson={admin.data.lesson_id}
        >
          <Section>
            <SectionTitle>{t('admin.common.overview')}</SectionTitle>
            <FieldGrid>
              <Field>
                <FieldLabel>{t('admin.common.id')}</FieldLabel>
                <FieldValue>{admin.data.id}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.slug')}</FieldLabel>
                <FieldValue>{admin.data.slug || '—'}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.order')}</FieldLabel>
                <FieldValue>{admin.data.order}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.status')}</FieldLabel>
                <FieldValue>{admin.data.status}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.topics.lesson')}</FieldLabel>
                <FieldValue>
                  <Link
                    to="/admin/lessons/$lessonId/"
                    params={{ lessonId: String(admin.data.lesson_id) }}
                  >
                    #{admin.data.lesson_id}
                  </Link>
                </FieldValue>
              </Field>
            </FieldGrid>
          </Section>

          <Section>
            <SectionTitle>{t('admin.topics.content')}</SectionTitle>
            {/* biome-ignore lint/security/noDangerouslySetInnerHtml: rendering trusted topic HTML in admin */}
            <ContentBody dangerouslySetInnerHTML={{ __html: contentHtml }} />
          </Section>
        </CourseOutlineLayout>
      )}
    </DetailPage>
  );
}

function TopicEditor({
  topic,
  onDone,
}: {
  topic: TopicAdmin;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [title, setTitle] = useState(topic.title);
  const [slug, setSlug] = useState(topic.slug);
  const [order, setOrder] = useState(String(topic.order));
  const [status, setStatus] = useState(topic.status);
  const [content, setContent] = useState(topic.content);
  const [lessonId, setLessonId] = useState(topic.lesson_id);
  // Null until changed: the field shows where the topic already is.
  const [position, setPosition] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  // A published version's topics keep their place and status; those change in a
  // new version. Wording still edits in place.
  const frozen = topic.version_state !== 'draft';
  const course = courseOf(topic.path);
  const { data: versions } = useCourseVersions(course?.id ?? null);
  const inDraftVersion =
    topic.version_state === 'draft' &&
    Boolean(versions?.some((v) => v.state === 'live'));
  const outline = api.adminGetCourseOutline.useQuery(
    {
      path: { course_id: course?.id ?? 0 },
      query: { version: course?.version ?? 1 },
    },
    { enabled: inDraftVersion && course !== null },
  );

  const lessonTopics =
    outline.data?.lessons.find((l) => l.id === lessonId)?.topics ?? [];
  const currentIndex = lessonTopics.findIndex((t) => t.id === topic.id);
  // In another lesson it goes last unless told otherwise.
  const lastPosition =
    currentIndex >= 0 ? lessonTopics.length : lessonTopics.length + 1;
  const shownPosition =
    position ??
    (outline.data
      ? String(currentIndex >= 0 ? currentIndex + 1 : lastPosition)
      : '');

  const formValues = JSON.stringify([
    title,
    slug,
    order,
    status,
    content,
    lessonId,
    position,
  ]);
  const [savedValues] = useState(formValues);
  const dirty = formValues !== savedValues;
  const {
    allowLeave,
    confirmDiscard,
    dialog: unsavedDialog,
  } = useUnsavedChanges(dirty);

  const invalidateTopic = () => {
    queryClient.invalidateQueries({
      queryKey: api.adminGetTopic.getQueryKey({ path: { topic_id: topic.id } }),
    });
    queryClient.invalidateQueries({
      queryKey: api.topicDetail.getQueryKey({ path: { topic_id: topic.id } }),
    });
    queryClient.invalidateQueries({
      queryKey: api.lessonTopics.getQueryKey({
        path: { lesson_id: topic.lesson_id },
      }),
    });
  };

  const updateMutation = api.adminUpdateTopic.useMutation(undefined, {
    onSuccess: () => {
      allowLeave();
      invalidateTopic();
      onDone();
    },
  });

  const deleteMutation = api.adminDeleteTopic.useMutation(undefined, {
    onSuccess: () => {
      allowLeave();
      queryClient.invalidateQueries({
        queryKey: api.lessonTopics.getQueryKey({
          path: { lesson_id: topic.lesson_id },
        }),
      });
      navigate({ to: '/admin/topics/', search: { lesson: topic.lesson_id } });
    },
  });

  const trimmedTitle = title.trim();

  // Publishing stamps the topic for good, so its quiz lock is confirmed first. In
  // a draft of a live course nothing locks until the version itself is published.
  const locksQuiz =
    !inDraftVersion &&
    status === 'published' &&
    topic.status !== 'published' &&
    !topic.quiz_locked;

  // Pinned when editing starts: a refetch (e.g. on tab focus) must not advance it,
  // or a save over someone else's change would slip past the conflict check.
  const [baseRevision] = useState(topic.revision);
  const handleSave = () => {
    if (!trimmedTitle || updateMutation.isPending) return;
    if (locksQuiz && !confirming) {
      setConfirming(true);
      return;
    }
    setConfirming(false);
    updateMutation.mutate({
      path: { topic_id: topic.id },
      body: {
        title: trimmedTitle,
        slug,
        // In a draft, Position places it; a stale order would move it back.
        ...(frozen
          ? {}
          : inDraftVersion
            ? { status }
            : { order: Number(order), status }),
        ...(inDraftVersion && lessonId !== topic.lesson_id
          ? { lesson_id: lessonId }
          : {}),
        ...(inDraftVersion && position ? { position: Number(position) } : {}),
        content,
        expected_revision: baseRevision,
      },
    });
  };

  const handleDelete = () => {
    if (!window.confirm(deletePrompt('topic', topic))) return;
    deleteMutation.mutate({ path: { topic_id: topic.id } });
  };

  const reload = () => {
    allowLeave();
    invalidateTopic();
    onDone();
  };

  return (
    <EditorLayout>
      <EditorMain>
        <LiveEditNotice state={topic.version_state} />
        <FormSection
          tone="content"
          icon={faFileLines}
          title={t('admin.common.details')}
          description={t('admin.topics.detailsDescription')}
        >
          <FormRow>
            <FormLabel htmlFor="topic-title">
              {t('admin.common.title')}
            </FormLabel>
            <TextInput
              id="topic-title"
              value={title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTitle(e.target.value)
              }
            />
          </FormRow>
          <FormRow>
            <FormLabel htmlFor="topic-slug">{t('admin.common.slug')}</FormLabel>
            <TextInput
              id="topic-slug"
              value={slug}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSlug(e.target.value)
              }
            />
          </FormRow>
        </FormSection>

        <FormSection
          tone="feedback"
          icon={faBookOpen}
          title={t('admin.topics.content')}
          description={t('admin.topics.contentDescription')}
        >
          <RichTextEditor
            value={topic.content}
            onChange={setContent}
            ariaLabel={t('admin.topics.contentAria')}
          />
        </FormSection>
      </EditorMain>

      <EditorPanel aria-label={t('admin.series.saveAndSettings')}>
        <SaveGroup
          heading={<PanelLabel>{t('admin.topics.panelLabel')}</PanelLabel>}
          dirty={dirty}
          saving={updateMutation.isPending}
          saveDisabled={!trimmedTitle}
          onSave={handleSave}
          onCancel={() => confirmDiscard(onDone)}
        >
          {!trimmedTitle && (
            <FieldHint>{t('admin.topics.giveTitle')}</FieldHint>
          )}
          {updateMutation.isError &&
            (editConflict(updateMutation.error) ? (
              <EditConflictNotice
                error={updateMutation.error}
                onReload={reload}
              />
            ) : (
              <ErrorText>
                {apiErrorDetail(updateMutation.error) ??
                  t('admin.common.couldNotSave')}
              </ErrorText>
            ))}
        </SaveGroup>

        {frozen ? (
          <PanelGroup>
            {course && (
              <NeedsNewVersion
                target={{
                  courseId: course.id,
                  version: course.version,
                  item: { entity: 'topic', lineageId: topic.lineage_id },
                }}
              >
                {t('admin.topics.frozenNotice')}
              </NeedsNewVersion>
            )}
            <FieldHint>
              {t('admin.topics.statusOrder', {
                status: topic.status,
                order: topic.order,
                interpolation: { escapeValue: false },
              })}
            </FieldHint>
          </PanelGroup>
        ) : (
          <PanelGroup>
            <FormRow>
              <FormLabel htmlFor="topic-status">
                {t('admin.common.status')}
              </FormLabel>
              <Select
                id="topic-status"
                value={status}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setStatus(e.target.value as TopicAdmin['status'])
                }
              >
                <option value="draft">draft</option>
                <option value="published">published</option>
              </Select>
              {inDraftVersion && status === 'draft' && (
                <FieldHint>{t('admin.topics.draftHidden')}</FieldHint>
              )}
              {locksQuiz && (
                <QuizPublishWarning
                  parent="topic"
                  publishedCount={topic.quiz_published_count}
                  draftCount={topic.quiz_draft_count}
                />
              )}
              <DraftAncestorNotice
                entityLabel="topic"
                ancestors={[
                  {
                    label: t('admin.entity.lesson'),
                    status: topic.lesson_status,
                  },
                  // In a draft version the course is a draft by definition.
                  ...(inDraftVersion
                    ? []
                    : [
                        {
                          label: t('admin.entity.course'),
                          status: topic.course_status,
                        },
                      ]),
                  {
                    label: t('admin.entity.series'),
                    status: topic.series_status,
                  },
                ]}
              />
            </FormRow>
            {inDraftVersion ? (
              <>
                <FormRow>
                  <FormLabel htmlFor="topic-lesson">
                    {t('admin.topics.lesson')}
                  </FormLabel>
                  <Select
                    id="topic-lesson"
                    value={lessonId}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      setLessonId(Number(e.target.value));
                      // Its place in the old lesson means nothing in the new one.
                      setPosition(null);
                    }}
                  >
                    {(outline.data?.lessons ?? []).map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.title}
                      </option>
                    ))}
                  </Select>
                </FormRow>
                <FormRow>
                  <FormLabel htmlFor="topic-position">
                    {t('admin.topics.positionInLesson')}
                  </FormLabel>
                  <TextInput
                    id="topic-position"
                    type="number"
                    min={1}
                    max={lastPosition}
                    value={shownPosition}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setPosition(e.target.value)
                    }
                    onBlur={() =>
                      position !== null &&
                      setPosition(clampPosition(position, lastPosition))
                    }
                  />
                </FormRow>
              </>
            ) : (
              <FormRow>
                <FormLabel htmlFor="topic-order">
                  {t('admin.common.order')}
                </FormLabel>
                <TextInput
                  id="topic-order"
                  type="number"
                  value={order}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setOrder(e.target.value)
                  }
                />
              </FormRow>
            )}
          </PanelGroup>
        )}

        {!frozen && (
          <DangerGroup>
            <Button
              className={`${buttonStyles.Button} ${buttonStyles.Danger}`}
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending
                ? t('admin.common.deleting')
                : t('admin.topics.delete')}
            </Button>
            {deleteMutation.isError && (
              <ErrorText>
                {apiErrorDetail(deleteMutation.error) ??
                  t('admin.topics.deleteError')}
              </ErrorText>
            )}
          </DangerGroup>
        )}
      </EditorPanel>

      <ConfirmDialog
        open={confirming}
        title={t('admin.series.confirmTitle')}
        confirmLabel={t('admin.common.publish')}
        onConfirm={handleSave}
        onCancel={() => setConfirming(false)}
      >
        <p>
          {quizPublishMessage({
            parent: 'topic',
            publishedCount: topic.quiz_published_count,
            draftCount: topic.quiz_draft_count,
          })}
        </p>
        <p>{t('admin.topics.returningNote')}</p>
      </ConfirmDialog>
      {unsavedDialog}
    </EditorLayout>
  );
}

const ContentBody = styled.div`
  ${richContentCss}
  padding: var(--inner-padding);
`;

const Message = styled.div<{ $error?: boolean }>`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
  padding: 2rem;
  text-align: center;
`;
